/**
 * Last Visited Products functionality
 * Tracks and displays recently viewed products using localStorage
 * Integrates with Swiper for carousel display
 */

(async () => {
  if (!customElements.get('last-visited-products')) {
    // Import Swiper
    const { default: Swiper } = await import('swiper');

    class LastVisitedProducts extends HTMLElement {
      constructor() {
        super();

        this.debug = true; // Enable debugging
        this.log('LastVisitedProducts: Constructor called');

        this.swiperElement = this.querySelector('[data-swiper-last-visited]');
        this.swiperWrapper = this.querySelector('[data-last-visited-products]');
        this.emptyState = this.querySelector('.last-visited-empty');
        this.placeholder = this.querySelector('.last-visited-placeholder');

        this.maxProducts = parseInt(this.dataset.maxProducts) || 8;
        this.currentProduct = this.dataset.currentProduct || null;
        this.storageKey = 'hairgivers_last_visited_products';

        this.log('Configuration:', {
          maxProducts: this.maxProducts,
          currentProduct: this.currentProduct,
          storageKey: this.storageKey,
          swiperElement: !!this.swiperElement,
          swiperWrapper: !!this.swiperWrapper,
          emptyState: !!this.emptyState
        });

        this.swiperOptions = {
          loop: false,
          autoplay: false,
          direction: 'horizontal',
          spaceBetween: 16,
          breakpoints: {
            320: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 }
          }
        };

        // Initialize Swiper
        this.log('Initializing Swiper with options:', this.swiperOptions);
        this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);

        // Check if section should be shown immediately
        this.log('Checking section visibility');
        this.checkSectionVisibility();

        // Track current product and load visited products
        // Check if page is already loaded or wait for load event
        if (document.readyState === 'complete') {
          this.log('Page already loaded - starting product tracking and loading immediately');
          this.trackCurrentProduct();
          this.loadLastVisitedProducts();
        } else {
          this.log('Waiting for page to load');
          window.addEventListener('load', () => {
            this.log('Window loaded - starting product tracking and loading');
            this.trackCurrentProduct();
            this.loadLastVisitedProducts();
          });
        }
      }

      /**
       * Debug logging helper
       */
      log(...args) {
        if (this.debug) {
          console.log('[LastVisitedProducts]', ...args);
        }
      }

      /**
       * Check if section should be visible based on stored products
       */
      checkSectionVisibility() {
        const visitedProducts = this.getStoredProducts();
        const filteredProducts = visitedProducts.filter(p => p.handle !== this.currentProduct);

        this.log('checkSectionVisibility:', {
          totalVisited: visitedProducts.length,
          filteredCount: filteredProducts.length,
          currentProduct: this.currentProduct,
          visitedProducts: visitedProducts.map(p => p.handle)
        });

        if (filteredProducts.length > 0) {
          this.log('Found products to display - section will be shown after content is ready');
          // Don't show section here - wait until loadLastVisitedProducts completes
        } else {
          this.log('Not showing section - no products to display');
          this.hideSection(); // Ensure section stays hidden
        }
      }

      /**
       * Show the section by adding has-products class and removing hidden class
       */
      showSection() {
        this.classList.add('has-products');
        this.classList.remove('last-visited-products--hidden');
        this.log('showSection: Section is now visible');
      }

      /**
       * Hide the section by removing has-products class and adding hidden class
       */
      hideSection() {
        this.classList.remove('has-products');
        this.classList.add('last-visited-products--hidden');
        this.log('hideSection: Section is now hidden');
      }

      /**
       * Track the current product being viewed
       */
      trackCurrentProduct() {
        this.log('trackCurrentProduct called for:', this.currentProduct);

        if (!this.currentProduct) {
          this.log('No current product to track');
          return;
        }

        const productData = this.getCurrentProductData();
        if (!productData) {
          this.log('Failed to get current product data');
          return;
        }

        this.log('Current product data:', productData);

        let visitedProducts = this.getStoredProducts();
        this.log('Existing visited products:', visitedProducts.length);

        // Remove current product if already in list
        const beforeFilter = visitedProducts.length;
        visitedProducts = visitedProducts.filter(p => p.handle !== this.currentProduct);
        this.log('After removing current product:', visitedProducts.length, 'removed:', beforeFilter - visitedProducts.length);

        // Add current product to beginning
        visitedProducts.unshift(productData);
        this.log('After adding current product to front:', visitedProducts.length);

        // Limit to max products
        visitedProducts = visitedProducts.slice(0, this.maxProducts * 2); // Store more than we show
        this.log('After limiting to max products:', visitedProducts.length, 'max:', this.maxProducts * 2);

        try {
          localStorage.setItem(this.storageKey, JSON.stringify(visitedProducts));
          this.log('Successfully saved to localStorage');
        } catch (error) {
          this.log('Error saving to localStorage:', error);
        }
      }

      /**
       * Get current product data from page
       */
      getCurrentProductData() {
        this.log('getCurrentProductData: Looking for product JSON');

        const productJson = document.querySelector('[data-product-json]');
        if (!productJson) {
          this.log('getCurrentProductData: No product JSON element found');
          return null;
        }

        this.log('getCurrentProductData: Found product JSON element');

        try {
          const product = JSON.parse(productJson.textContent);
          const productData = {
            id: product.id,
            handle: product.handle,
            title: product.title,
            price: product.price,
            compare_at_price: product.compare_at_price,
            featured_image: product.featured_image,
            url: product.url || `/products/${product.handle}`, // Fallback URL construction
            available: product.available,
            variants: product.variants || [], // Store variants for ATC form
            tags: product.tags || [], // Store tags for discount prevention logic
            timestamp: Date.now()
          };

          this.log('getCurrentProductData: Successfully parsed product data:', {
            handle: productData.handle,
            title: productData.title,
            price: productData.price
          });

          return productData;
        } catch (e) {
          this.log('getCurrentProductData: Error parsing product JSON:', e);
          console.error('Error parsing product JSON:', e);
          return null;
        }
      }

      /**
       * Get stored products from localStorage
       */
      getStoredProducts() {
        try {
          const stored = localStorage.getItem(this.storageKey);
          const products = stored ? JSON.parse(stored) : [];
          this.log('getStoredProducts: Retrieved', products.length, 'products from localStorage');
          return products;
        } catch (e) {
          this.log('getStoredProducts: Error reading localStorage:', e);
          console.error('Error reading localStorage:', e);
          return [];
        }
      }

      /**
       * Load and display last visited products using client-side rendering
       */
      loadLastVisitedProducts() {
        this.log('loadLastVisitedProducts: Starting client-side rendering');

        const visitedProducts = this.getStoredProducts();
        this.log('loadLastVisitedProducts: Retrieved', visitedProducts.length, 'visited products');

        // Filter out current product and limit results
        const filteredProducts = visitedProducts
          .filter(p => p.handle !== this.currentProduct)
          .slice(0, this.maxProducts);

        this.log('loadLastVisitedProducts: After filtering and limiting:', {
          originalCount: visitedProducts.length,
          filteredCount: filteredProducts.length,
          currentProduct: this.currentProduct,
          maxProducts: this.maxProducts,
          filteredHandles: filteredProducts.map(p => p.handle)
        });

        if (filteredProducts.length === 0) {
          this.log('loadLastVisitedProducts: No products to show, displaying empty state');
          this.showEmptyState();
          return;
        }

        // Find the template product card
        const templateCard = this.swiperWrapper.querySelector('.swiper-slide');
        if (!templateCard) {
          this.log('loadLastVisitedProducts: No template card found');
          this.showEmptyState();
          return;
        }

        this.log('loadLastVisitedProducts: Found template card, starting to clone and populate');

        // Clear the wrapper and populate with visited products
        this.swiperWrapper.innerHTML = '';

        // Create and populate all cards first (without showing them)
        const populatedCards = [];
        filteredProducts.forEach((productData, index) => {
          this.log(`loadLastVisitedProducts: Processing product ${index + 1}:`, productData.handle);
          const clonedCard = this.createProductCardFromTemplate(templateCard, productData);
          if (clonedCard) {
            populatedCards.push(clonedCard);
          }
        });

        this.log('loadLastVisitedProducts: All cards processed, adding to DOM');

        // Add all populated cards to the DOM
        populatedCards.forEach(card => {
          this.swiperWrapper.appendChild(card);
        });

        this.log('loadLastVisitedProducts: All cards added to DOM, now showing section');

        // Now show the section and update swiper (after all cards are ready)
        this.showSection();
        this.swiperElement.style.display = 'block';
        this.emptyState.style.display = 'none';
        this.swiperInstance.update();

        // Use theme's global price update function to handle all dynamic pricing
        this.updateAllPricesUsingTheme();

        this.log('loadLastVisitedProducts: Section is now visible with', populatedCards.length, 'product cards and dynamic pricing updated');
      }


      /**
       * Create a product card from template using stored product data
       */
      createProductCardFromTemplate(templateCard, productData) {
        this.log('createProductCardFromTemplate: Creating card for', productData.handle);

        // Clone the template card
        const clonedCard = templateCard.cloneNode(true);

        try {
          // Update product image
          this.updateProductImage(clonedCard, productData);

          // Update product title and links
          this.updateProductLinks(clonedCard, productData);

          // Update product price
          this.updateProductPrice(clonedCard, productData);

          // Update product badges (sold out, sale, etc.)
          this.updateProductBadges(clonedCard, productData);

          // Update additional product information
          this.updateProductAdditionalInfo(clonedCard, productData);

          // Update or remove add-to-cart form
          this.updateAddToCartForm(clonedCard, productData);

          this.log('createProductCardFromTemplate: Successfully created card for', productData.handle);
          return clonedCard;

        } catch (error) {
          this.log('createProductCardFromTemplate: Error creating card for', productData.handle, error);
          return null;
        }
      }

      /**
       * Update product image in cloned card - handles complex format-image.liquid structure
       */
      updateProductImage(card, productData) {
        this.log('updateProductImage: Updating images for', productData.handle);
        this.log('updateProductImage: Full product data:', productData);

        // Try to get image URL from featured_image first, then fallback to images array
        let baseImageUrl = null;

        if (productData.featured_image && productData.featured_image.url) {
          baseImageUrl = productData.featured_image.url;
          this.log('updateProductImage: Using featured_image URL:', baseImageUrl);
        } else if (productData.featured_image && typeof productData.featured_image === 'string') {
          // Sometimes featured_image is just a string URL
          baseImageUrl = productData.featured_image;
          this.log('updateProductImage: Using featured_image as string URL:', baseImageUrl);
        } else if (productData.images && productData.images.length > 0) {
          // Fallback to first image in images array
          const firstImage = productData.images[0];
          if (firstImage && firstImage.url) {
            baseImageUrl = firstImage.url;
            this.log('updateProductImage: Using first image from images array:', baseImageUrl);
          } else if (typeof firstImage === 'string') {
            baseImageUrl = firstImage;
            this.log('updateProductImage: Using first image string from images array:', baseImageUrl);
          }
        }

        if (!baseImageUrl) {
          this.log('updateProductImage: No image URL found for', productData.handle, 'in any format');
          this.log('updateProductImage: Featured image:', productData.featured_image);
          this.log('updateProductImage: Images array:', productData.images);
          return;
        }

        this.log('updateProductImage: Final base image URL:', baseImageUrl);

        // Find the picture elements (format-image.liquid creates picture > source + img structure)
        const pictureElements = card.querySelectorAll('picture');
        const imageLinks = card.querySelectorAll('a.product-card__image, a[class*="product-card__image"]');

        this.log('updateProductImage: Found', pictureElements.length, 'picture elements and', imageLinks.length, 'image links');

        // Update each picture element
        pictureElements.forEach((picture, pictureIndex) => {
          this.log(`updateProductImage: Processing picture element ${pictureIndex + 1}`);

          // Find source elements within this picture
          const sourceElements = picture.querySelectorAll('source');
          const imgElement = picture.querySelector('img');

          this.log(`updateProductImage: Found ${sourceElements.length} source elements and`, imgElement ? '1' : '0', 'img element');

          // Update source elements (responsive breakpoints)
          sourceElements.forEach((source, sourceIndex) => {
            const oldSrcset = source.srcset;

            // Extract sizes from existing srcset to maintain responsive behavior
            const newSrcset = this.generateSrcset(baseImageUrl, oldSrcset);
            source.srcset = newSrcset;

            this.log(`updateProductImage: Updated source ${sourceIndex + 1} srcset:`, {
              from: oldSrcset,
              to: newSrcset
            });
          });

          // Update main img element
          if (imgElement) {
            const oldSrc = imgElement.src;
            const oldSrcset = imgElement.srcset;

            // Update src with appropriate size
            imgElement.src = this.createSizedImageUrl(baseImageUrl, '364x406'); // Default product card size
            imgElement.alt = productData.title;

            // Update srcset if it exists
            if (oldSrcset) {
              imgElement.srcset = this.generateSrcset(baseImageUrl, oldSrcset);
              this.log(`updateProductImage: Updated img srcset:`, {
                from: oldSrcset,
                to: imgElement.srcset
              });
            }

            this.log(`updateProductImage: Updated main img:`, {
              from: oldSrc,
              to: imgElement.src,
              alt: productData.title
            });
          }
        });

        // Update image links
        const productUrl = productData.url || `/products/${productData.handle}`;
        imageLinks.forEach((link, index) => {
          const oldHref = link.href;
          link.href = productUrl;
          this.log(`updateProductImage: Updated image link ${index + 1}:`, { from: oldHref, to: productUrl });
        });
      }

      /**
       * Create properly sized image URL for Shopify CDN
       */
      createSizedImageUrl(baseUrl, size) {
        // Remove any existing size parameters
        let cleanUrl = baseUrl.split('?')[0]; // Remove query params
        cleanUrl = cleanUrl.replace(/_\d+x\d*\./, '.'); // Remove existing size like _400x400.
        cleanUrl = cleanUrl.replace(/_\d*x\d+\./, '.'); // Remove existing size like _x400.

        // Add the new size before the file extension
        const sizeParam = '_' + size;
        cleanUrl = cleanUrl.replace(/\.([^.]+)$/, sizeParam + '.$1');

        return cleanUrl;
      }

      /**
       * Generate srcset with proper Shopify image URLs based on existing pattern
       */
      generateSrcset(baseUrl, existingSrcset) {
        if (!existingSrcset) return '';

        // Parse existing srcset to extract sizes and pixel densities
        const srcsetParts = existingSrcset.split(',').map(part => part.trim());
        const newSrcsetParts = [];

        srcsetParts.forEach(part => {
          const [url, descriptor] = part.split(' ');

          // Extract size information from existing URL to maintain consistency
          const sizeMatch = url.match(/_(\d+x\d*|\d*x\d+)\./);
          let size = '364x406'; // Default size

          if (sizeMatch) {
            size = sizeMatch[1];
          } else {
            // Try to infer size from descriptor
            if (descriptor === '2x') {
              size = '728x812'; // Double the default size for 2x
            }
          }

          const newUrl = this.createSizedImageUrl(baseUrl, size);
          newSrcsetParts.push(`${newUrl} ${descriptor}`);
        });

        return newSrcsetParts.join(', ');
      }

      /**
       * Update product links and titles in cloned card
       */
      updateProductLinks(card, productData) {
        this.log('updateProductLinks: Updating links for', productData.handle);

        // Ensure we have a valid URL
        const productUrl = productData.url || `/products/${productData.handle}`;
        this.log('updateProductLinks: Using URL:', productUrl);

        // Update all links to point to the product
        const productLinks = card.querySelectorAll('a[href]');
        this.log('updateProductLinks: Found', productLinks.length, 'links to update');

        productLinks.forEach((link, index) => {
          const oldHref = link.href;

          // Only update if it's a product-related link (not external links)
          if (link.href.includes('/products/') || link.classList.contains('full-unstyled-link') || link.classList.contains('product-card__image')) {
            link.href = productUrl;
            this.log(`updateProductLinks: Updated link ${index + 1}:`, {
              from: oldHref,
              to: productUrl,
              classes: link.className
            });
          } else {
            this.log(`updateProductLinks: Skipped external link ${index + 1}:`, oldHref);
          }
        });

        // Update product title
        const titleElement = card.querySelector('.product-card__title');
        if (titleElement) {
          titleElement.textContent = productData.title;
          this.log('updateProductLinks: Updated title to:', productData.title);
        } else {
          this.log('updateProductLinks: No title element found');
        }

        // Update visually hidden title for accessibility (but be selective)
        const hiddenTitles = card.querySelectorAll('.visually-hidden');
        hiddenTitles.forEach((hiddenTitle, index) => {
          // Only update if it contains a product title (not generic text like "Sale price")
          const text = hiddenTitle.textContent.trim();
          if (text !== '' && !text.toLowerCase().includes('price') && !text.toLowerCase().includes('regular') && !text.toLowerCase().includes('sale')) {
            const oldText = hiddenTitle.textContent;
            hiddenTitle.textContent = productData.title;
            this.log(`updateProductLinks: Updated hidden title ${index + 1}:`, {
              from: oldText,
              to: productData.title
            });
          }
        });
      }

      /**
       * Update product price by recreating the format-price structure with correct data
       */
      updateProductPrice(card, productData) {
        const priceContainer = card.querySelector('.product-card__price');
        if (!priceContainer) {
          this.log('updateProductPrice: No price container found for', productData.handle);
          return;
        }

        this.log('updateProductPrice: Updating price for', productData.handle, {
          price: productData.price,
          compare_at_price: productData.compare_at_price,
          available: productData.available,
          tags: productData.tags
        });

        // Create a temporary product object that matches Shopify's product structure
        const tempProduct = {
          id: productData.id,
          price: productData.price,
          compare_at_price: productData.compare_at_price || null,
          available: productData.available,
          price_varies: false,
          compare_at_price_varies: false,
          tags: productData.tags || []
        };

        // Check if product has tags that prevent discount
        const productTags = tempProduct.tags.join(',').toLowerCase();
        const preventDiscount = productTags.includes('prevent-discount') || productTags.includes('prevent_discount') || productTags.includes('set');

        // Use the same logic as format-price.liquid to generate the structure
        const price = this.formatPrice(tempProduct.price);
        const comparePrice = (tempProduct.compare_at_price && tempProduct.compare_at_price > tempProduct.price)
          ? this.formatPrice(tempProduct.compare_at_price)
          : null;

        const isOnSale = comparePrice !== null;
        const isAvailable = tempProduct.available;

        // Generate price structure matching format-price.liquid exactly
        const priceClasses = [
          'price',
          'is-flex',
          'has-col-gap-sm',
          'is-flex-wrap',
          'is-directioned-column',
          'text-small'
        ];

        if (!isAvailable) {
          priceClasses.push('price--sold-out');
        }
        if (isOnSale) {
          priceClasses.push('price--on-sale');
        }

        let priceHtml = `<div class="${priceClasses.join(' ')}">`;
        priceHtml += '<dl>';

        if (isOnSale) {
          // Sale price structure
          priceHtml += `
            <div class="price__sale is-flex is-flex-wrap is-directioned-row">
              <dt><span class="visually-hidden visually-hidden--inline">Sale price</span></dt>
              <dd><span class="price-item price-item--sale">${price}</span></dd>
              <dt><span class="visually-hidden visually-hidden--inline">Regular price</span></dt>
              <dd class="price__compare"><s class="price-item price-item--regular">${comparePrice}</s></dd>
            </div>
          `;
        } else {
          // Regular price with tiered pricing capability
          priceHtml += `
            <tiered-pricing-price
              class="price__sale is-flex is-flex-wrap is-directioned-row"
              data-price-regular="${tempProduct.price}"
              data-price-regular-money="${price}"
              data-prevent-discount="${preventDiscount}"
            >
              <dt><span class="visually-hidden visually-hidden--inline">Sale price</span></dt>
              <dd><span class="price-item price-item--sale" data-tiered-price-sale>${price}</span></dd>
              <dt><span class="visually-hidden visually-hidden--inline">Regular price</span></dt>
              <dd class="price__compare"><s class="price-item price-item--regular" data-tiered-price-regular></s></dd>
            </tiered-pricing-price>
          `;
        }

        priceHtml += '</dl></div>';

        // Replace the price container content
        priceContainer.innerHTML = priceHtml;

        this.log('updateProductPrice: Successfully updated price structure for', productData.handle);
      }

      /**
       * Update product badges in cloned card
       */
      updateProductBadges(card, productData) {
        // Look for both possible badge containers
        const badgesContainer = card.querySelector('.product__badges');
        const badgesSection = card.querySelector('.badges');

        this.log('updateProductBadges: Looking for badges containers for', productData.handle, {
          productBadges: !!badgesContainer,
          badgesSection: !!badgesSection,
          available: productData.available,
          compare_at_price: productData.compare_at_price,
          price: productData.price,
          tags: productData.tags
        });

        // Find the main badges container to update
        const mainBadgesContainer = badgesSection || badgesContainer;

        if (!mainBadgesContainer) {
          this.log('updateProductBadges: No badges container found for', productData.handle);
          return;
        }

        // Find existing tiered pricing badge to preserve it
        const tieredPricingBadge = mainBadgesContainer.querySelector('tiered-pricing-badge');
        let tieredBadgeHtml = '';
        if (tieredPricingBadge) {
          // Update the tiered pricing badge data attributes
          tieredPricingBadge.dataset.preventDiscount = (productData.tags && productData.tags.some(tag =>
            ['prevent-discount', 'prevent_discount', 'set'].includes(tag.toLowerCase())
          )).toString();
          tieredBadgeHtml = tieredPricingBadge.outerHTML;
        }

        const badges = [];

        // Add tiered pricing badge first if it exists
        if (tieredBadgeHtml) {
          badges.push(tieredBadgeHtml);
        }

        // Add product tags as badges (like "Set" badge)
        if (productData.tags && productData.tags.length > 0) {
          productData.tags.forEach(tag => {
            const tagLower = tag.toLowerCase();
            // Add common product badges based on tags
            if (tagLower === 'set') {
              badges.push(`<span class="product__badge product__badge--set">${tag}</span>`);
            }
            if (tagLower === 'new') {
              badges.push(`<span class="product__badge product__badge--new">New</span>`);
            }
            if (tagLower === 'bestseller') {
              badges.push(`<span class="product__badge product__badge--bestseller">Bestseller</span>`);
            }
          });
        }

        // Add sale badge if on sale
        if (productData.compare_at_price && productData.compare_at_price > productData.price) {
          badges.push(`<span class="product__badge product__badge--sale">Sale</span>`);
        }

        // Add sold out badge if not available
        if (!productData.available) {
          badges.push(`<span class="product__badge product__badge--sold-out">Sold out</span>`);
        }

        // Apply all badges
        if (badges.length > 0) {
          mainBadgesContainer.innerHTML = badges.join('\n  ');
          this.log('updateProductBadges: Applied', badges.length, 'badges for', productData.handle);
        } else {
          this.log('updateProductBadges: No badges to apply for', productData.handle);
        }
      }

      /**
       * Update additional product information (keep it generic or use product type)
       */
      updateProductAdditionalInfo(card, productData) {
        const additionalInfoElement = card.querySelector('.product-card__additional-information');
        if (!additionalInfoElement) {
          this.log('updateProductAdditionalInfo: No additional info element found for', productData.handle);
          return;
        }

        this.log('updateProductAdditionalInfo: Updating additional info for', productData.handle);

        // Since we don't have complete variant data, show basic product info if available
        let additionalInfo = '';

        // You could add product type, vendor, or other basic info here
        if (productData.product_type) {
          additionalInfo += productData.product_type;
        }
        if (productData.vendor && productData.vendor !== productData.product_type) {
          additionalInfo += additionalInfo ? `, ${productData.vendor}` : productData.vendor;
        }

        // If we have no specific info, leave it as is (don't clear)
        if (additionalInfo) {
          additionalInfoElement.textContent = additionalInfo;
          this.log('updateProductAdditionalInfo: Updated additional info for', productData.handle, 'to:', additionalInfo);
        } else {
          this.log('updateProductAdditionalInfo: Keeping existing additional info for', productData.handle);
        }
      }

      /**
       * Update the add-to-cart form and show button for the visited product
       */
      updateAddToCartForm(card, productData) {
        const productForm = card.querySelector('product-form');
        const showButton = card.querySelector('.product-card__show_button');
        const atcContainer = card.querySelector('.product-card__atc');
        const addButton = card.querySelector('button[name="add"][type="submit"]');

        this.log('updateAddToCartForm: Updating form for', productData.handle, {
          available: productData.available,
          id: productData.id,
          hasForm: !!productForm,
          hasShowButton: !!showButton,
          hasAtcContainer: !!atcContainer
        });

        // Update the show button URL to point to the correct product
        if (showButton) {
          const productUrl = productData.url || `/products/${productData.handle}`;
          showButton.href = productUrl;

          // Update button text and state based on availability
          if (!productData.available) {
            showButton.textContent = 'Sold out';
            showButton.classList.add('button--disabled');
            showButton.setAttribute('disabled', 'disabled');
          } else {
            showButton.textContent = 'Show';
            showButton.classList.remove('button--disabled');
            showButton.removeAttribute('disabled');
          }

          this.log('updateAddToCartForm: Updated show button for', productData.handle);
        }

        // Update the ATC form if present
        if (productForm && productData.available) {
          this.log('updateAddToCartForm: Updating form fields for', productData.handle, {
            productId: productData.id,
            variantsCount: productData.variants ? productData.variants.length : 0
          });

          // Update product ID in hidden inputs
          const productIdInput = productForm.querySelector('input[name="product-id"]');
          if (productIdInput) {
            productIdInput.value = productData.id;
            this.log('updateAddToCartForm: Updated product-id input to', productData.id);
          }

          // Update the main variant ID (use first available variant)
          const variantIdInput = productForm.querySelector('input[name="id"]');
          if (variantIdInput) {
            let variantId = null;

            if (productData.variants && productData.variants.length > 0) {
              // Find first available variant or fallback to first variant
              const firstAvailableVariant = productData.variants.find(v => v.available) || productData.variants[0];
              variantId = firstAvailableVariant.id;
            } else {
              // If no variants stored, construct a fallback variant ID from product ID
              // Note: This might not be accurate - ideally we should store variant data
              this.log('updateAddToCartForm: No variants data available for', productData.handle);
            }

            if (variantId) {
              variantIdInput.value = variantId;
              this.log('updateAddToCartForm: Updated variant id input to', variantId);
            }
          }

          // Update form action to point to correct product (optional safety measure)
          const form = productForm.querySelector('form');
          if (form) {
            // Keep the same action (/cart/add) but ensure form has correct product data
            form.action = '/cart/add';
          }

          // Update form labels and IDs with product info
          const labels = productForm.querySelectorAll('label');
          labels.forEach(label => {
            if (label.textContent.trim() && !label.textContent.toLowerCase().includes('quantity')) {
              label.textContent = productData.title;
            }
          });

          // Update quantity input ID to be unique
          const quantityInput = productForm.querySelector('input[name="quantity"]');
          if (quantityInput) {
            const newId = `Quantity-last-visited-${productData.handle}`;
            quantityInput.id = newId;

            const quantityLabel = productForm.querySelector('label[for*="Quantity"]');
            if (quantityLabel) {
              quantityLabel.setAttribute('for', newId);
            }
          }

          // Show the ATC container if it was hidden
          if (atcContainer) {
            atcContainer.style.display = '';
          }

          this.log('updateAddToCartForm: Successfully updated ATC form for', productData.handle);
        } else if (atcContainer && !productData.available) {
          // Hide ATC for sold out products
          atcContainer.style.display = 'none';
          this.log('updateAddToCartForm: Hid ATC for sold out product', productData.handle);
        }

        // Update ATC button state
        if (addButton) {
          if (!productData.available) {
            addButton.disabled = true;
            addButton.classList.add('button--disabled');
          } else {
            addButton.disabled = false;
            addButton.classList.remove('button--disabled');
          }
        }
      }

      /**
       * Format price using theme's global pricing configuration directly
       * Avoids HTMLElement constructor issues by using window.tieredPricing config
       */
      formatPrice(cents) {
        // Use the global pricing configuration directly
        if (!window.tieredPricing) {
          this.log('formatPrice: window.tieredPricing not available, using fallback');
          return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
        }

        const { locale, money_format } = window.tieredPricing;

        // Get cart currency from cookies (same logic as tieredPricing class)
        const value = `; ${document.cookie}`;
        const parts = value.split(`; cart_currency=`);
        const cart_currency = parts.length === 2 ? parts.pop().split(';').shift() : 'EUR';

        // Currency rules (same as theme)
        const currenciessWithoutCents = "SEK HUF NOK ISK";
        const currenciesWithCommaCentSeperator = "SEK EUR CZK DKK NOK PLN RON";
        const currenciesWithPrefix = "€ $ ¥ £ EUR CHF";

        const withoutCents = currenciessWithoutCents.includes(cart_currency);
        const withComma = currenciesWithCommaCentSeperator.includes(cart_currency);
        const currencyPrefix = currenciesWithPrefix.includes(cart_currency);

        // Format number using Intl.NumberFormat
        const formatter = new Intl.NumberFormat(locale, {
          minimumFractionDigits: withoutCents ? 0 : 2,
          maximumFractionDigits: withoutCents ? 0 : 2,
        });
        const formattedNumber = formatter.format(cents / 100);

        // Apply money format (same logic as tieredPricing class)
        const moneyFormat = money_format.replace('{{amount_with_comma_separator}}', '').replace('{{amount}}', '');
        let finalPrice = currencyPrefix ?
          `${moneyFormat}${formattedNumber}`.replace('{{amount_no_decimals}}', '') :
          `${formattedNumber}${moneyFormat}`.replace('{{amount_no_decimals}}', '');

        // Apply comma separator if needed
        if (withComma) {
          finalPrice = finalPrice.replace('.', ',');
        }

        this.log('formatPrice: Formatted', cents, 'to', finalPrice);
        return finalPrice;
      }

      /**
       * Update all prices using theme's global price update system
       * This ensures tiered pricing works correctly for all product cards
       */
      updateAllPricesUsingTheme() {
        this.log('updateAllPricesUsingTheme: Triggering global price updates');

        // Use the theme's existing handleAllPricesAndBadges function
        // This will automatically find and update all tiered-pricing-price elements
        if (window.tieredPricing && window.tieredPricing.tiers) {
          const tiers = window.tieredPricing.tiers;
          const pricesElements = this.querySelectorAll('tiered-pricing-price');
          const badgesElements = this.querySelectorAll('tiered-pricing-badge');

          this.log('updateAllPricesUsingTheme: Found', pricesElements.length, 'price elements and', badgesElements.length, 'badge elements');

          // Apply the same logic as handleAllPricesAndBadges from product-form-cart-page.js
          tiers.forEach(({ tiered_pricing_threshold, tiered_pricing_discount }) => {
            pricesElements.forEach(element => {
              if (element.handlePrice) {
                element.handlePrice(false, tiered_pricing_threshold, tiered_pricing_discount);
              }
            });
            badgesElements.forEach(element => {
              if (element.handleBadge) {
                element.handleBadge(false, tiered_pricing_threshold, tiered_pricing_discount);
              }
            });
          });

          this.log('updateAllPricesUsingTheme: Successfully applied tiered pricing to all elements');
        } else {
          this.log('updateAllPricesUsingTheme: No tiered pricing configuration found');
        }
      }

      /**
       * Show empty state when no products available
       */
      showEmptyState() {
        this.log('showEmptyState: Displaying empty state');
        this.swiperElement.style.display = 'none';
        this.emptyState.style.display = 'block';

        // Hide the entire section since there are no products
        this.hideSection();
      }
    }

    // Register the custom element
    window.LastVisitedProducts = LastVisitedProducts;
    customElements.define('last-visited-products', LastVisitedProducts);
  }
})();